import { FundraisingFund, FUND_PRIORITIES } from '../../types';
import { Card } from "@repo/o37-ui";
import { Globe, Building2, Clock } from "lucide-react";

interface KanbanCardProps {
  fund: FundraisingFund;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, fund: FundraisingFund) => void;
  onDragEnd: () => void;
  onClick: () => void;
  compact?: boolean;
}

export default function KanbanCard({ 
  fund, 
  isDragging, 
  onDragStart, 
  onDragEnd, 
  onClick,
  compact = false 
}: KanbanCardProps) {
  const priorityConfig = FUND_PRIORITIES.find(p => p.priority === fund.priority);
  const primaryContact = fund.contacts?.find(c => c.isPrimary) || fund.contacts?.[0];
  const termSheet = fund.termSheets?.find(ts => ts.status !== 'declined' && ts.status !== 'expired');

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (compact) {
    return (
      <div
        className={`bg-white rounded-lg border border-neutral-100 p-2 cursor-grab active:cursor-grabbing transition-all duration-100 hover:shadow-sm ${
          isDragging ? 'opacity-40 scale-[0.98]' : ''
        }`}
        draggable
        onDragStart={(e) => onDragStart(e, fund)}
        onDragEnd={onDragEnd}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          {priorityConfig && (
            <div className={`w-1.5 h-1.5 rounded-full ${priorityConfig.color}`} />
          )}
          <div className="text-xs font-medium text-neutral-900 truncate flex-1">{fund.name}</div>
        </div>
      </div>
    );
  }

  return (
    <Card
      className={`p-3 cursor-grab active:cursor-grabbing transition-all duration-100 hover:shadow-sm group ${
        isDragging ? 'opacity-40 scale-[0.98]' : ''
      }`}
      draggable
      onDragStart={(e: React.DragEvent<HTMLDivElement>) => onDragStart(e, fund)}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        {fund.logoUrl ? (
          <img 
            src={fund.logoUrl} 
            alt={fund.name} 
            className="w-8 h-8 rounded-md object-cover border border-neutral-100"
          />
        ) : (
          <div className="w-8 h-8 rounded-md bg-neutral-50 border border-neutral-100 flex items-center justify-center text-[10px] font-medium text-neutral-500">
            {fund.name.charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-neutral-900 truncate">{fund.name}</div>
            {priorityConfig && (
              <div 
                className={`w-2 h-2 rounded-full flex-shrink-0 ${priorityConfig.color}`}
                title={priorityConfig.label}
              />
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-0.5">
             {primaryContact ? (
                <span className="truncate">{primaryContact.name}</span>
             ) : (
                <span className="text-neutral-400 italic">No contact</span>
             )}
             {(fund.website || fund.updatedAt) && <span className="text-neutral-300">•</span>}
             {fund.updatedAt && (
               <div className="flex items-center gap-1 flex-shrink-0" title={`Last updated: ${new Date(fund.updatedAt).toLocaleString()}`}>
                 <Clock className="w-3 h-3" />
                 <span>{formatTimeAgo(fund.updatedAt)}</span>
               </div>
             )}
          </div>
        </div>
      </div>

      {/* Thesis / Description */}
      {fund.thesis && (
        <div className="mb-2.5">
          <p className="text-[11px] text-neutral-600 line-clamp-2 leading-relaxed">
            {fund.thesis}
          </p>
        </div>
      )}

      {/* Footer Details */}
      <div className="flex items-center gap-2 pt-2 border-t border-neutral-100 mt-2">
        {/* Investment Range */}
        <div className="flex-1 min-w-0">
          {(fund.typicalTicketMin || fund.typicalTicketMax) ? (
             <div className="text-[10px] font-medium text-neutral-700 truncate" title="Typical Ticket Size">
               {fund.typicalTicketMin && fund.typicalTicketMax ? (
                 `${formatCurrency(fund.typicalTicketMin)} - ${formatCurrency(fund.typicalTicketMax)}`
               ) : fund.typicalTicketMin ? (
                 `${formatCurrency(fund.typicalTicketMin)}+`
               ) : (
                 `Up to ${formatCurrency(fund.typicalTicketMax!)}`
               )}
             </div>
          ) : (
            <div className="text-[10px] text-neutral-400 italic">No ticket info</div>
          )}
        </div>

        {/* Portfolio Count */}
        {fund.portfolioCompanies && fund.portfolioCompanies.length > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-neutral-500 px-1.5 py-0.5 bg-neutral-50 rounded" title={`${fund.portfolioCompanies.length} portfolio companies`}>
            <Building2 className="w-3 h-3" />
            <span>{fund.portfolioCompanies.length}</span>
          </div>
        )}

        {/* Website Link */}
        {fund.website && (
            <a 
              href={fund.website} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-neutral-400 hover:text-blue-600 transition-colors p-1 -mr-1 rounded hover:bg-blue-50"
              title="Visit Website"
            >
              <Globe className="w-3.5 h-3.5" />
            </a>
        )}

        {/* Active Term Sheet Badge */}
        {termSheet && (
          <div className="flex-shrink-0">
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
              {formatCurrency(termSheet.investmentAmount)}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
